import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DateInput from "@/components/common/DateInput";
import SalespersonSelect from "@/components/common/SalespersonSelect";
import { Button } from "@/components/ui/button";
import { PERIODS, todayIST } from "@/lib/dateRange";
import { CalendarDays, RotateCcw, Globe2 } from "lucide-react";

export const canFilterSalesperson = (role) => ["super_admin", "manager", "finance_manager"].includes(role);

function CustomDates({ filters, setFilters, dayOnly }) {
  const setStart = (v) => setFilters(dayOnly ? { start: v, end: v } : { start: v, end: filters.end && filters.end < v ? v : filters.end });
  const setEnd = (v) => setFilters({ end: v, start: filters.start && filters.start > v ? v : filters.start });
  return (
    <div className="flex items-center gap-2 w-full sm:w-auto">
      <DateInput value={filters.start} max={dayOnly ? undefined : filters.end || undefined} onChange={setStart}
        className="flex-1 sm:w-40" testid="filter-start-date" placeholder={dayOnly ? "Pick a day" : "From"} />
      {!dayOnly && (
        <>
          <span className="text-xs text-slate-400">to</span>
          <DateInput value={filters.end} min={filters.start || undefined} onChange={setEnd}
            className="flex-1 sm:w-40" testid="filter-end-date" placeholder="To" />
        </>
      )}
    </div>
  );
}

export default function FilterBar({ filters, setFilters, periods, dayOnly = false, showSalesperson = true, children, testid = "filter-bar" }) {
  const { user } = useAuth();
  const opts = PERIODS.filter((p) => !periods || periods.includes(p.value));
  const onPeriod = (v) => {
    if (v !== "custom") return setFilters({ period: v, start: "", end: "" });
    const d = filters.start || todayIST();
    setFilters({ period: v, start: d, end: dayOnly ? d : filters.end || d });
  };
  const reset = () => setFilters({ period: "", start: "", end: "", salesperson: "" });

  return (
    <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm p-3 sm:p-4 flex flex-col xl:flex-row xl:items-center gap-3" data-testid={testid}>
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 flex-1">
        <Select value={filters.period} onValueChange={onPeriod}>
          <SelectTrigger className="h-11 w-full sm:w-44" data-testid="filter-period">
            <CalendarDays className="h-4 w-4 text-slate-400 shrink-0" /><SelectValue />
          </SelectTrigger>
          <SelectContent>
            {opts.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`filter-period-${o.value}`}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {filters.period === "custom" && <CustomDates filters={filters} setFilters={setFilters} dayOnly={dayOnly} />}
        {showSalesperson && canFilterSalesperson(user?.role) && (
          <SalespersonSelect value={filters.salesperson} onChange={(v) => setFilters({ salesperson: v === "all" ? "" : v })} />
        )}
        {children}
      </div>
      <div className="flex items-center justify-between sm:justify-end gap-3">
        <span className="text-xs text-slate-400 inline-flex items-center gap-1" data-testid="filter-tz-label"><Globe2 className="h-3.5 w-3.5" /> IST · Asia/Kolkata</span>
        <Button variant="ghost" size="sm" onClick={reset} className="text-slate-500 hover:text-teal" data-testid="filter-reset-btn"><RotateCcw className="h-4 w-4" /> Reset</Button>
      </div>
    </div>
  );
}
